import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"
import { 
  Brain, Target, Rocket, Trophy, Users, LineChart, 
  ArrowRight, CheckCircle, Sparkles, Compass, BookOpen,
  X, Minus, Plus, RotateCcw
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ParticleBackground } from './animations/ParticleBackground';
import { SectionWrapper } from './shared/SectionWrapper';
import { SectionTransition } from '@/components/shared/SectionTransition';
import { HoverEffect } from '@/components/animations/HoverEffect';
import { FloatingAnimation } from '@/components/animations/FloatingAnimation';

// Expanded feature details
const FEATURE_DETAILS = {
  "AI-Powered Guidance": {
    demo: {
      title: "AI Career Assistant",
      description: "Interactive AI chat that provides personalized career guidance",
      examples: [
        "Career path recommendations based on your skills and interests",
        "Interview preparation with AI-powered mock interviews",
        "Salary negotiation strategies tailored to your industry",
        "Custom learning path recommendations"
      ],
      preview: (
        <div className="space-y-4">
          {/* Chat Interface */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="space-y-4">
              {/* AI Messages */}
              <motion.div 
                className="flex items-start gap-3"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Brain className="w-4 h-4 text-blue-400" />
                </div>
                <div className="flex-1 space-y-2">
                  <p className="text-sm text-gray-300">
                    Hi! I'm your AI Career Assistant. What would you like help with today?
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      "Career Planning",
                      "Skill Assessment",
                      "Interview Prep",
                      "Salary Insights"
                    ].map((option, i) => (
                      <motion.button
                        key={i}
                        className="px-3 py-1 text-sm rounded-full bg-blue-500/10 
                                 text-blue-400 hover:bg-blue-500/20 transition-colors"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: i * 0.1 }}
                      >
                        {option}
                      </motion.button>
                    ))}
                  </div>
                </div>
              </motion.div>

              {/* Sample Conversation */}
              <motion.div
                className="space-y-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
              >
                <div className="flex justify-end">
                  <div className="bg-primary-500/10 text-primary-400 rounded-lg px-4 py-2 text-sm">
                    I'd like help with career planning
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                    <Brain className="w-4 h-4 text-blue-400" />
                  </div>
                  <div className="flex-1">
                    <motion.div
                      className="space-y-3"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.8 }}
                    >
                      <p className="text-sm text-gray-300">
                        Great! Let's create a personalized career plan. Based on your profile:
                      </p>
                      <ul className="space-y-2 text-sm">
                        <motion.li 
                          className="flex items-center gap-2 text-blue-400"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1 }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Strong skills in digital marketing
                        </motion.li>
                        <motion.li 
                          className="flex items-center gap-2 text-green-400"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.2 }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          3 years of experience
                        </motion.li>
                        <motion.li 
                          className="flex items-center gap-2 text-purple-400"
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 1.4 }}
                        >
                          <CheckCircle className="w-4 h-4" />
                          Interest in data analytics
                        </motion.li>
                      </ul>
                    </motion.div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              className="p-3 rounded-lg bg-white/5 hover:bg-white/10 
                         border border-white/10 hover:border-white/20 
                         transition-all group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500/20 
                               flex items-center justify-center">
                  <Target className="w-4 h-4 text-green-400" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-300">
                    Start Assessment
                  </div>
                  <div className="text-xs text-gray-400">
                    15 min quick evaluation
                  </div>
                </div>
              </div>
            </motion.button>

            <motion.button
              className="p-3 rounded-lg bg-white/5 hover:bg-white/10 
                         border border-white/10 hover:border-white/20 
                         transition-all group"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-purple-500/20 
                               flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-purple-400" />
                </div>
                <div className="text-left">
                  <div className="text-sm font-medium text-gray-300">
                    View Roadmap
                  </div>
                  <div className="text-xs text-gray-400">
                    Personalized path
                  </div>
                </div>
              </div>
            </motion.button>
          </div>
        </div>
      )
    }
  },
  "Skill Tracking": {
    demo: {
      title: "Interactive Skill Dashboard",
      description: "Track and visualize your skill development progress",
      examples: [
        "Real-time skill proficiency tracking",
        "Personalized improvement recommendations",
        "Industry benchmark comparisons",
        "Skill gap analysis"
      ],
      preview: (
        <div className="space-y-4">
          {/* Skill Overview */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h6 className="text-sm font-medium text-gray-300">Skills Overview</h6>
                <select className="text-xs bg-white/5 border border-white/10 rounded-md px-2 py-1 text-gray-400">
                  <option>Last 30 days</option>
                  <option>Last 90 days</option>
                  <option>All time</option>
                </select>
              </div>

              {/* Skill Progress Bars */}
              {[
                { 
                  name: "React Development", 
                  level: 75, 
                  growth: 15,
                  color: "from-blue-400 to-cyan-500"
                },
                { 
                  name: "UI/UX Design", 
                  level: 60, 
                  growth: 10,
                  color: "from-purple-400 to-pink-500"
                },
                { 
                  name: "Project Management", 
                  level: 85, 
                  growth: 5,
                  color: "from-amber-400 to-orange-500"
                }
              ].map((skill, i) => (
                <motion.div
                  key={i}
                  className="space-y-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-300">{skill.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-primary-400">{skill.level}%</span>
                      <span className="text-green-400 text-xs">
                        +{skill.growth}%
                      </span>
                    </div>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full bg-gradient-to-r ${skill.color}`}
                      initial={{ width: "0%" }}
                      animate={{ width: `${skill.level}%` }}
                      transition={{ duration: 1, delay: i * 0.2 }}
                    />
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Skill Insights */}
          <div className="grid grid-cols-2 gap-3">
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-sm font-medium text-gray-300 mb-2">
                Industry Benchmark
              </div>
              <div className="text-2xl font-bold text-primary-400">
                Top 15%
              </div>
              <div className="text-xs text-gray-400">
                Among similar profiles
              </div>
            </motion.div>

            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="text-sm font-medium text-gray-300 mb-2">
                Next Milestone
              </div>
              <div className="text-2xl font-bold text-primary-400">
                23 days
              </div>
              <div className="text-xs text-gray-400">
                To reach next level
              </div>
            </motion.div>
          </div>
        </div>
      )
    }
  },
  "Achievement System": {
    demo: {
      title: "Career Achievements & Rewards",
      description: "Gamified system to track and celebrate your career milestones",
      examples: [
        "Unlock badges for completed milestones",
        "Earn rewards for consistent progress",
        "Track achievement streaks",
        "Share accomplishments with your network"
      ],
      preview: (
        <div className="space-y-4">
          {/* Achievement Showcase */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h6 className="text-sm font-medium text-gray-300">Recent Achievements</h6>
              <span className="text-xs text-primary-400">Level 12</span>
            </div>
            
            {/* Achievement Grid */}
            <div className="grid grid-cols-3 gap-4">
              {[
                { 
                  icon: <Trophy className="w-6 h-6" />, 
                  title: "Quick Learner", 
                  progress: "3/5",
                  color: "amber",
                  unlocked: true
                },
                { 
                  icon: <Target className="w-6 h-6" />, 
                  title: "Goal Setter", 
                  progress: "Complete!",
                  color: "green",
                  unlocked: true
                },
                { 
                  icon: <Sparkles className="w-6 h-6" />, 
                  title: "Rising Star", 
                  progress: "2/3",
                  color: "purple",
                  unlocked: false
                }
              ].map((achievement, i) => (
                <motion.div
                  key={i}
                  className={`p-3 bg-white/5 rounded-lg text-center relative 
                             ${achievement.unlocked ? 'cursor-pointer' : 'opacity-50'}`}
                  whileHover={achievement.unlocked ? { scale: 1.05 } : {}}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.1 }}
                >
                  <motion.div
                    className={`w-12 h-12 mx-auto mb-2 rounded-full 
                               bg-${achievement.color}-500/20 
                               flex items-center justify-center 
                               text-${achievement.color}-400`}
                    animate={achievement.unlocked ? {
                      scale: [1, 1.1, 1],
                      rotate: [0, 5, -5, 0]
                    } : {}}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatType: "reverse"
                    }}
                  >
                    {achievement.icon}
                  </motion.div>
                  <div className="text-sm text-gray-300">{achievement.title}</div>
                  <div className="text-xs text-primary-400 mt-1">{achievement.progress}</div>
                  
                  {/* Glow Effect for Unlocked Achievements */}
                  {achievement.unlocked && (
                    <motion.div
                      className="absolute inset-0 -z-10 rounded-lg opacity-0"
                      animate={{
                        opacity: [0, 0.2, 0],
                        boxShadow: [
                          `0 0 10px 2px var(--tw-colors-${achievement.color}-500)`,
                          `0 0 20px 4px var(--tw-colors-${achievement.color}-500)`,
                          `0 0 10px 2px var(--tw-colors-${achievement.color}-500)`
                        ]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        repeatType: "reverse"
                      }}
                    />
                  )}
                </motion.div>
              ))}
            </div>
          </div>

          {/* Progress Overview */}
          <div className="grid grid-cols-3 gap-3">
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4 }}
            >
              <div className="text-2xl font-bold text-primary-400">12</div>
              <div className="text-xs text-gray-400">Achievements</div>
            </motion.div>
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
            >
              <div className="text-2xl font-bold text-green-400">5</div>
              <div className="text-xs text-gray-400">Day Streak</div>
            </motion.div>
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-center"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="text-2xl font-bold text-purple-400">85%</div>
              <div className="text-xs text-gray-400">Completion</div>
            </motion.div>
          </div>
        </div>
      )
    }
  },
  "Progress Analytics": {
    demo: {
      title: "Career Growth Analytics",
      description: "Comprehensive analytics to track your professional development",
      examples: [
        "Visual progress tracking over time",
        "Skill development analytics",
        "Learning activity insights",
        "Performance trend analysis"
      ],
      preview: (
        <div className="space-y-4">
          {/* Main Chart */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h6 className="text-sm font-medium text-gray-300">Growth Trajectory</h6>
              <div className="flex gap-2">
                {["1M", "3M", "6M", "1Y"].map((period, i) => (
                  <button
                    key={i}
                    className={`px-2 py-1 text-xs rounded-md transition-colors
                               ${i === 2 ? 
                                 'bg-primary-500/20 text-primary-400' : 
                                 'text-gray-400 hover:bg-white/5'}`}
                  >
                    {period}
                  </button>
                ))}
              </div>
            </div>

            <div className="h-48 relative">
              {/* Chart visualization */}
              <div className="absolute inset-0 flex items-end">
                {[40, 35, 60, 45, 70, 80].map((height, i) => (
                  <motion.div
                    key={i}
                    className="flex-1 mx-1"
                    style={{
                      background: `linear-gradient(to top, rgb(34, 197, 94, 0.2), rgb(16, 185, 129, 0.05))`
                    }}
                    initial={{ height: 0 }}
                    animate={{ height: `${height}%` }}
                    transition={{ duration: 1, delay: i * 0.1 }}
                  >
                    <motion.div
                      className="h-1 bg-green-500"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: i * 0.1 + 0.5 }}
                    />
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="flex justify-between text-xs text-gray-400 mt-2">
              <span>Jan</span>
              <span>Feb</span>
              <span>Mar</span>
              <span>Apr</span>
              <span>May</span>
              <span>Jun</span>
            </div>
          </div>

          {/* Key Metrics */}
          <div className="grid grid-cols-2 gap-3">
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.7 }}
            >
              <div className="text-sm text-gray-400 mb-1">Growth Rate</div>
              <div className="text-2xl font-bold text-green-400">+27%</div>
              <div className="text-xs text-gray-400">vs last month</div>
            </motion.div>

            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.8 }}
            >
              <div className="text-sm text-gray-400 mb-1">Learning Hours</div>
              <div className="text-2xl font-bold text-primary-400">48.5</div>
              <div className="text-xs text-gray-400">this month</div>
            </motion.div>
          </div>
        </div>
      )
    }
  },
  "Career Mapping": {
    demo: {
      title: "Interactive Career Roadmap",
      description: "Visual career planning and milestone tracking",
      examples: [
        "Customizable career path visualization",
        "Milestone tracking and planning",
        "Industry-specific career ladders",
        "Progress timeline view"
      ],
      preview: (
        <div className="space-y-4">
          {/* Career Path Visualization */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h6 className="text-sm font-medium text-gray-300">Your Career Journey</h6>
              <select className="text-xs bg-white/5 border border-white/10 rounded-md px-2 py-1 text-gray-400">
                <option>Software Development</option>
                <option>Product Management</option>
                <option>Data Science</option>
              </select>
            </div>

            <div className="relative">
              <div className="absolute left-4 inset-y-0 w-0.5 bg-gradient-to-b from-red-500/50 to-transparent" />
              
              {[
                { 
                  level: "Junior Developer", 
                  status: "Completed",
                  time: "2021-2022",
                  skills: ["HTML/CSS", "JavaScript", "React Basics"]
                },
                { 
                  level: "Mid-Level Developer", 
                  status: "Current",
                  time: "2022-Present",
                  skills: ["React Advanced", "Node.js", "System Design"]
                },
                { 
                  level: "Senior Developer", 
                  status: "Next Goal",
                  time: "Estimated: 2024",
                  skills: ["Architecture", "Team Leadership", "Project Management"]
                },
                { 
                  level: "Tech Lead", 
                  status: "Future Goal",
                  time: "Target: 2025",
                  skills: ["System Architecture", "Team Management", "Strategic Planning"]
                }
              ].map((level, i) => (
                <motion.div
                  key={i}
                  className="relative pl-8 pb-8"
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.2 }}
                >
                  <motion.div 
                    className={`absolute left-3 w-3 h-3 rounded-full 
                              ${level.status === 'Completed' ? 'bg-green-500' :
                                level.status === 'Current' ? 'bg-primary-500' :
                                'bg-gray-500'} -translate-x-1.5`}
                    whileHover={{ scale: 1.2 }}
                    animate={level.status === 'Current' ? {
                      scale: [1, 1.2, 1],
                      boxShadow: [
                        '0 0 0 0 rgba(59, 130, 246, 0.4)',
                        '0 0 0 10px rgba(59, 130, 246, 0)',
                      ]
                    } : {}}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  />
                  
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <h6 className="text-sm font-medium text-gray-300">{level.level}</h6>
                      <span className={`text-xs px-2 py-1 rounded-full
                        ${level.status === 'Completed' ? 'bg-green-500/20 text-green-400' :
                          level.status === 'Current' ? 'bg-primary-500/20 text-primary-400' :
                          'bg-gray-500/20 text-gray-400'}`}>
                        {level.status}
                      </span>
                    </div>
                    <div className="text-xs text-gray-400">{level.time}</div>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {level.skills.map((skill, j) => (
                        <motion.span
                          key={j}
                          className="text-xs px-2 py-1 rounded-full bg-white/5 text-gray-400"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: i * 0.2 + j * 0.1 }}
                        >
                          {skill}
                        </motion.span>
                      ))}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-3 gap-3">
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1 }}
            >
              <div className="text-2xl font-bold text-primary-400">2.5y</div>
              <div className="text-xs text-gray-400">Experience</div>
            </motion.div>
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1 }}
            >
              <div className="text-2xl font-bold text-green-400">8</div>
              <div className="text-xs text-gray-400">Skills Mastered</div>
            </motion.div>
            <motion.div
              className="p-3 rounded-lg bg-white/5 border border-white/10 text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.2 }}
            >
              <div className="text-2xl font-bold text-purple-400">4</div>
              <div className="text-xs text-gray-400">Certifications</div>
            </motion.div>
          </div>
        </div>
      )
    }
  },
  "Learning Paths": {
    demo: {
      title: "Personalized Learning Journey",
      description: "Curated learning paths tailored to your career goals",
      examples: [
        "Custom curriculum development",
        "Progress tracking for each course",
        "Resource recommendations",
        "Learning schedule optimization"
      ],
      preview: (
        <div className="space-y-4">
          {/* Current Learning Path */}
          <div className="p-4 bg-white/5 rounded-lg border border-white/10">
            <div className="flex items-center justify-between mb-4">
              <h6 className="text-sm font-medium text-gray-300">Frontend Development Path</h6>
              <span className="text-xs text-primary-400">65% Complete</span>
            </div>

            {/* Course List */}
            {[
              { 
                title: "Advanced React Patterns", 
                progress: 80,
                status: "In Progress",
                duration: "2h remaining",
                color: "blue"
              },
              { 
                title: "State Management", 
                progress: 40,
                status: "Started",
                duration: "4h remaining",
                color: "purple"
              },
              { 
                title: "Performance Optimization", 
                progress: 0,
                status: "Not Started",
                duration: "6h total",
                color: "gray"
              }
            ].map((course, i) => (
              <motion.div
                key={i}
                className="mb-4 last:mb-0"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <motion.div
                      className={`w-2 h-2 rounded-full bg-${course.color}-400`}
                      animate={course.status === 'In Progress' ? {
                        scale: [1, 1.5, 1],
                        opacity: [1, 0.5, 1]
                      } : {}}
                      transition={{ duration: 2, repeat: Infinity }}
                    />
                    <span className="text-sm text-gray-300">{course.title}</span>
                  </div>
                  <span className="text-xs text-gray-400">{course.duration}</span>
                </div>
                
                <div className="relative h-2 bg-white/5 rounded-full overflow-hidden">
                  <motion.div
                    className={`absolute inset-y-0 left-0 bg-gradient-to-r from-${course.color}-400 to-${course.color}-500`}
                    initial={{ width: 0 }}
                    animate={{ width: `${course.progress}%` }}
                    transition={{ duration: 1, delay: i * 0.2 }}
                  />
                </div>
              </motion.div>
            ))}
          </div>

          {/* Learning Resources */}
          <div className="grid grid-cols-2 gap-3">
            <motion.button
              className="p-3 rounded-lg bg-white/5 hover:bg-white/10 
                         border border-white/10 hover:border-white/20 
                         transition-all group text-left"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 
                               flex items-center justify-center">
                  <BookOpen className="w-4 h-4 text-indigo-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-300">
                    Course Library
                  </div>
                  <div className="text-xs text-gray-400">
                    200+ courses
                  </div>
                </div>
              </div>
            </motion.button>

            <motion.button
              className="p-3 rounded-lg bg-white/5 hover:bg-white/10 
                         border border-white/10 hover:border-white/20 
                         transition-all group text-left"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-500/20 
                               flex items-center justify-center">
                  <Target className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <div className="text-sm font-medium text-gray-300">
                    Practice Projects
                  </div>
                  <div className="text-xs text-gray-400">
                    45 available
                  </div>
                </div>
              </div>
            </motion.button>
          </div>
        </div>
      )
    }
  }
} as const;

// Update the FEATURES array to include modalContent
const FEATURES = [
  {
    icon: <Brain className="h-6 w-6" />,
    title: "AI-Powered Guidance",
    description: "Get personalized career advice and learning recommendations tailored to your goals",
    gradient: "from-blue-400 to-cyan-500",
    glow: "blue",
    modalContent: FEATURE_DETAILS["AI-Powered Guidance"]
  },
  {
    icon: <Target className="h-6 w-6" />,
    title: "Skill Tracking",
    description: "Monitor your progress and identify key areas for improvement",
    gradient: "from-purple-400 to-pink-500",
    glow: "purple",
    modalContent: FEATURE_DETAILS["Skill Tracking"]
  },
  {
    icon: <Trophy className="h-6 w-6" />,
    title: "Achievement System",
    description: "Earn badges and track your career milestones",
    gradient: "from-amber-400 to-orange-500",
    glow: "orange",
    modalContent: FEATURE_DETAILS["Achievement System"]
  },
  {
    icon: <LineChart className="h-6 w-6" />,
    title: "Progress Analytics",
    description: "Visualize your growth with detailed analytics and insights",
    gradient: "from-green-400 to-emerald-500",
    glow: "green",
    modalContent: FEATURE_DETAILS["Progress Analytics"]
  },
  {
    icon: <Compass className="h-6 w-6" />,
    title: "Career Mapping",
    description: "Plan your career path with interactive roadmaps",
    gradient: "from-red-400 to-rose-500",
    glow: "red",
    modalContent: FEATURE_DETAILS["Career Mapping"]
  },
  {
    icon: <BookOpen className="h-6 w-6" />,
    title: "Learning Paths",
    description: "Follow structured learning paths tailored to your goals",
    gradient: "from-indigo-400 to-violet-500",
    glow: "indigo",
    modalContent: FEATURE_DETAILS["Learning Paths"]
  }
] as const;

export function Features() {
  const [selectedFeature, setSelectedFeature] = useState<(typeof FEATURES)[number] | null>(null);
  const [scale, setScale] = useState(1);
  const [isDragging, setIsDragging] = useState(false);

  return (
    <>
      <section className="relative py-20 overflow-hidden">
        {/* Gradient Background with Animation */}
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900">
          <motion.div
            className="absolute inset-0 opacity-30"
            animate={{
              backgroundPosition: ['0% 0%', '100% 100%'],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              repeatType: "reverse",
              ease: "linear",
            }}
            style={{
              backgroundImage: 'radial-gradient(circle at center, rgba(59, 130, 246, 0.1) 0%, transparent 50%)',
              backgroundSize: '100% 100%',
            }}
          />
        </div>

        <div className="relative container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-16"
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Empower Your{' '}
              <span className="bg-gradient-to-r from-primary-400 to-primary-600 bg-clip-text text-transparent">
                Career Journey
              </span>
            </motion.h2>
            <motion.p 
              className="text-gray-400 text-lg max-w-2xl mx-auto"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
            >
              Discover powerful tools and features designed to accelerate your professional growth
            </motion.p>
          </motion.div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {FEATURES.map((feature, index) => (
              <motion.div
                key={index}
                onClick={() => setSelectedFeature(feature)}
                className="relative group cursor-pointer"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <div className={`relative p-6 rounded-xl bg-white/5 backdrop-blur-sm 
                                border border-white/10 h-full transition-all duration-300
                                hover:border-white/20 hover:bg-white/10`}>
                  {/* Glow Effect */}
                  <motion.div
                    className="absolute inset-0 -z-10 rounded-xl opacity-0 blur-xl
                               group-hover:opacity-20 transition-opacity duration-500"
                    animate={{
                      boxShadow: [
                        `0 0 20px 5px var(--tw-colors-${feature.glow}-500)`,
                        `0 0 30px 8px var(--tw-colors-${feature.glow}-500)`,
                        `0 0 20px 5px var(--tw-colors-${feature.glow}-500)`
                      ]
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut"
                    }}
                  />

                  {/* Feature Icon with Glow */}
                  <div className={`relative w-12 h-12 rounded-lg bg-gradient-to-br ${feature.gradient} 
                                  p-2.5 flex items-center justify-center text-white mb-4
                                  group-hover:scale-110 transition-transform duration-300`}>
                    {feature.icon}
                    <motion.div
                      className="absolute inset-0 -z-10 rounded-lg opacity-0 blur-md
                                 group-hover:opacity-100 transition-opacity duration-300"
                      animate={{
                        boxShadow: [
                          `0 0 10px 2px var(--tw-colors-${feature.glow}-500)`,
                          `0 0 15px 4px var(--tw-colors-${feature.glow}-500)`,
                          `0 0 10px 2px var(--tw-colors-${feature.glow}-500)`
                        ]
                      }}
                      transition={{
                        duration: 1.5,
                        repeat: Infinity,
                        ease: "easeInOut"
                      }}
                    />
                  </div>

                  {/* Feature Content */}
                  <h3 className={`text-xl font-semibold mb-2 bg-gradient-to-r ${feature.gradient} 
                                 bg-clip-text text-transparent`}>
                    {feature.title}
                  </h3>
                  <p className="text-gray-400">
                    {feature.description}
                  </p>

                  {/* Accent Line */}
                  <motion.div
                    className={`absolute bottom-0 left-0 h-0.5 w-0 bg-gradient-to-r ${feature.gradient}
                               group-hover:w-full transition-all duration-500`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Decorative Elements */}
        <motion.div
          className="absolute top-0 left-0 w-full h-px bg-gradient-to-r 
                     from-transparent via-primary-500/50 to-transparent"
          animate={{
            opacity: [0.3, 0.6, 0.3],
          }}
          transition={{
            duration: 3,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      </section>

      <Dialog open={selectedFeature !== null} onOpenChange={() => setSelectedFeature(null)}>
        <DialogContent className="bg-slate-900/95 backdrop-blur-xl border-white/10 max-w-4xl max-h-[90vh] overflow-auto">
          {selectedFeature && (
            <>
              <DialogHeader className="pb-2">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${selectedFeature.gradient} 
                                  p-2 flex items-center justify-center text-white`}>
                    {selectedFeature.icon}
                  </div>
                  <DialogTitle className={`text-xl font-bold bg-gradient-to-r ${selectedFeature.gradient} 
                                        bg-clip-text text-transparent`}>
                    {selectedFeature.title}
                  </DialogTitle>
                </div>
              </DialogHeader>

              {/* Interactive Demo Content */}
              <motion.div
                className="space-y-3"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {selectedFeature.modalContent?.demo && (
                  <>
                    <h4 className="text-base font-semibold text-white">
                      {selectedFeature.modalContent.demo.title}
                    </h4>
                    <DialogDescription className="text-sm text-gray-400">
                      {selectedFeature.modalContent.demo.description}
                    </DialogDescription>
                    
                    {/* Interactive Demo Preview with Zoom & Scroll */}
                    <motion.div
                      className="bg-slate-800/50 rounded-xl border border-white/5 overflow-hidden"
                      style={{ 
                        scale,
                        transformOrigin: "top center"
                      }}
                      drag
                      dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
                      dragElastic={0.1}
                      onDragStart={() => setIsDragging(true)}
                      onDragEnd={() => setTimeout(() => setIsDragging(false), 100)}
                    >
                      <div className="max-h-[40vh] overflow-auto custom-scrollbar p-4">
                        {selectedFeature.modalContent.demo.preview}
                      </div>
                    </motion.div>

                    {/* Examples */}
                    <div className="mt-2">
                      <h5 className="text-sm font-semibold text-gray-300 mb-1">
                        Key Features
                      </h5>
                      <ul className="space-y-0.5">
                        {selectedFeature.modalContent.demo.examples.map((example, i) => (
                          <motion.li
                            key={i}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: i * 0.1 }}
                            className="flex items-center gap-2 text-sm text-gray-400"
                          >
                            <CheckCircle className="w-3 h-3 text-primary-400" />
                            <span>{example}</span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </motion.div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}